import React, { useState, useMemo, useRef, useEffect, useLayoutEffect } from 'react';
import './CaseDetail.css';
import WinModal from './WinModal';
import { useTranslation } from 'react-i18next';

// Курс обмена: 1 TON = 269 звезд
const TON_TO_STARS_RATE = 269;

// Функция для расчета цены в TON
const calculateTonPrice = (starPrice) => {
  return starPrice > 0 ? (starPrice / TON_TO_STARS_RATE).toFixed(2) : 0;
};

const smallPrizes = [
    { name: 'Cup', price: 30, image: 'small-prize-cup.png' },
    { name: 'Rocket', price: 50, image: 'small-prize-rocket.png' },
    { name: 'Ring', price: 100, image: 'small-prize-ring.png' },
    { name: 'Heart', price: 15, image: 'small-prize-heart.png' },
    { name: 'Bear', price: 15, image: 'small-prize-bear.png' },
    { name: 'Gift', price: 25, image: 'small-prize-gift.png' },
    { name: 'Rose', price: 25, image: 'small-prize-rose.png' },
    { name: 'Cake Slice', price: 50, image: 'small-prize-cake-slice.png' },
    { name: 'Flowers', price: 25, image: 'small-prize-flowers.png' },
].map(prize => ({
    ...prize,
    tonPrice: calculateTonPrice(prize.price)
}));

const REEL_ITEM_WIDTH = 100 + 16; // 100px width + 16px margin

const FREE_CASE_COOLDOWN = 24 * 60 * 60 * 1000; // 24 часа в мс
const FREE_CASE_STORAGE_KEY = 'freeCaseNextAvailable';

const OFFSET_FIX = 8; // px, для точного центрирования

const CaseDetail = ({ caseItem, balance, onOpenBalanceModal, onAddStars }) => {
    const { t } = useTranslation();
    const [isSpinning, setIsSpinning] = useState(false);
    const [wonPrize, setWonPrize] = useState(null);
    const [reelTransform, setReelTransform] = useState('translateX(0px)');
    const [transitionEnabled, setTransitionEnabled] = useState(false); // Start with no transition for slow scroll
    const [freeCaseTimeLeft, setFreeCaseTimeLeft] = useState(0);
    const [freeCaseTimerId, setFreeCaseTimerId] = useState(null);

    // NEW: Refs and state for dynamic width calculation
    const reelContainerRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(0);

    const mainPrizes = useMemo(() => (caseItem.mainPrizes || []).map(p => ({...p, tonPrice: calculateTonPrice(p.price)})), [caseItem.mainPrizes]);
    
    const potentialPrizes = useMemo(() => {
      const casePrizes = (caseItem.potentialPrizes || mainPrizes);
      if (caseItem.name === 'Free Case' || caseItem._id === '6') {
        // Для Free Case объединяем potentialPrizes (звёзды) и smallPrizes
        const allPrizes = [...casePrizes, ...smallPrizes].reduce((acc, current) => {
          if (!acc.find(item => item.price === current.price && item.image === current.image)) {
            acc.push(current);
          }
          return acc;
        }, []);
        return allPrizes.map(p => ({...p, tonPrice: calculateTonPrice(p.price)}));
      }
      // Для остальных кейсов добавлять smallPrizes
      const allPrizes = [...casePrizes, ...smallPrizes].reduce((acc, current) => {
        if (!acc.find(item => item.name === current.name)) {
          acc.push(current);
        }
        return acc;
      }, []);
      return allPrizes.map(p => ({...p, tonPrice: calculateTonPrice(p.price)}));
    }, [caseItem.potentialPrizes, mainPrizes, caseItem.name, caseItem._id]);

    // Для остальных кейсов: делаем шансы так, чтобы средний выигрыш был больше цены кейса
    const adjustedPotentialPrizes = useMemo(() => {
      if (caseItem.name === 'Free Case' || caseItem._id === '6') return potentialPrizes;
      if (!potentialPrizes || !potentialPrizes.length) return potentialPrizes;
      // Главные призы с шансом 0%
      const mainPrizeImages = (caseItem.mainPrizes || []).map(p => p.image);
      // Оставляем только не-главные призы
      const nonMainPrizes = potentialPrizes.filter(p => !mainPrizeImages.includes(p.image));
      // Сортируем по цене по убыванию
      const sorted = [...nonMainPrizes].sort((a, b) => b.price - a.price);
      const casePrice = caseItem.price || 1;
      let prizes = [];
      let totalChance = 0;
      // Распределяем шансы так, чтобы средний выигрыш был ровно цена кейса
      // Даем шанс только самым дорогим призам, пока не достигнем нужного среднего
      let remainingChance = 100;
      let sum = 0;
      for (let i = 0; i < sorted.length; i++) {
        let maxPossibleChance = remainingChance;
        let neededChance = Math.round(((casePrice - sum) / sorted[i].price) * 100);
        let chance = Math.max(0, Math.min(maxPossibleChance, neededChance));
        if (i === sorted.length - 1) chance = remainingChance; // последний приз добирает остаток
        prizes.push({ ...sorted[i], chance });
        sum += sorted[i].price * (chance / 100);
        remainingChance -= chance;
      }
      // Добавляем главные призы с шансом 0%
      for (const main of (caseItem.mainPrizes || [])) {
        prizes.push({ ...main, chance: 0 });
      }
      return prizes;
    }, [potentialPrizes, caseItem.price, caseItem.name, caseItem._id, caseItem.mainPrizes]);

    const reelItems = useMemo(() => {
        const visualReelPool = [
            ...smallPrizes,
            ...mainPrizes, ...mainPrizes, ...mainPrizes, ...mainPrizes // Add main prizes multiple times
        ];
        const base = visualReelPool.sort(() => Math.random() - 0.5);
        return [...base, ...base, ...base, ...base, ...base];
    }, []);
    
    const prizesPerSegment = useMemo(() => reelItems.length / 5, [reelItems]);

    const animationFrameId = useRef(null);
    const scrollPosition = useRef(0);
    const lastTimestamp = useRef(null);
    
    // NEW: Get the actual container width on mount
    useLayoutEffect(() => {
        if (reelContainerRef.current) {
            setContainerWidth(reelContainerRef.current.clientWidth);
        }
    }, []);
    
    // Initialize position using dynamic width
    useEffect(() => {
        if (containerWidth === 0) return;
        const initialPosition = (prizesPerSegment * REEL_ITEM_WIDTH) - (containerWidth / 2) + (REEL_ITEM_WIDTH / 2);
        scrollPosition.current = -initialPosition;
        setReelTransform(`translateX(${scrollPosition.current}px)`);
    }, [prizesPerSegment, containerWidth]);

    // Animation Effect for slow scroll
    useEffect(() => {
        if (containerWidth === 0) return; // Don't start animation until width is known

        const slowScroll = (timestamp) => {
            if (lastTimestamp.current === null) {
                lastTimestamp.current = timestamp;
            }
            const deltaTime = timestamp - lastTimestamp.current;
            lastTimestamp.current = timestamp;

            if (!isSpinning) {
                const scrollSpeed = 15;
                scrollPosition.current -= (scrollSpeed * deltaTime) / 1000;
                
                const segmentWidth = prizesPerSegment * REEL_ITEM_WIDTH;
                const startOfResetSegment = -((prizesPerSegment * REEL_ITEM_WIDTH * 2) - (containerWidth / 2) + (REEL_ITEM_WIDTH / 2));

                if (scrollPosition.current < startOfResetSegment) {
                    scrollPosition.current += segmentWidth;
                }
                
                setReelTransform(`translateX(${scrollPosition.current}px)`);
            }
            animationFrameId.current = requestAnimationFrame(slowScroll);
        };

        animationFrameId.current = requestAnimationFrame(slowScroll);
        return () => {
            cancelAnimationFrame(animationFrameId.current);
            lastTimestamp.current = null;
        };
    }, [isSpinning, prizesPerSegment, containerWidth]);

    // Для Free Case: проверяем таймер при открытии
    useEffect(() => {
      if (caseItem.name === 'Free Case' || caseItem._id === '6') {
        const nextAvailable = parseInt(localStorage.getItem(FREE_CASE_STORAGE_KEY), 10) || 0;
        const now = Date.now();
        if (nextAvailable > now) {
          setFreeCaseTimeLeft(nextAvailable - now);
          const timer = setInterval(() => {
            const left = nextAvailable - Date.now();
            setFreeCaseTimeLeft(left > 0 ? left : 0);
            if (left <= 0) {
              clearInterval(timer);
              setFreeCaseTimerId(null);
            }
          }, 1000);
          setFreeCaseTimerId(timer);
          return () => clearInterval(timer);
        } else {
          setFreeCaseTimeLeft(0);
        }
      }
      // eslint-disable-next-line
    }, [caseItem.name, caseItem._id]);

    // Переопределяем selectWinner для обычных кейсов
    const selectWinner = () => {
      if (caseItem.name === 'Free Case' || caseItem._id === '6') {
        const totalChance = potentialPrizes.reduce((sum, prize) => sum + (prize.chance || 0), 0);
        let randomPoint = Math.random() * totalChance;
        for (const prize of potentialPrizes) {
          if (randomPoint < (prize.chance || 0)) return prize;
          randomPoint -= (prize.chance || 0);
        }
        return potentialPrizes[potentialPrizes.length - 1];
      } else {
        const totalChance = adjustedPotentialPrizes.reduce((sum, prize) => sum + (prize.chance || 1), 0);
        let randomPoint = Math.random() * totalChance;
        for (const prize of adjustedPotentialPrizes) {
          if (randomPoint < (prize.chance || 1)) return prize;
          randomPoint -= (prize.chance || 1);
        }
        return adjustedPotentialPrizes[adjustedPotentialPrizes.length - 1];
      }
    };

    const handleSpin = () => {
        if (isSpinning || containerWidth === 0) return;

        setTransitionEnabled(true);
        setIsSpinning(true);

        const targetItem = selectWinner(); // This still uses the correct, low probabilities
        const searchSegment = 3;
        let winnerIndex = -1;

        // Find the winning item in the visually-boosted reel
        const startIndex = searchSegment * prizesPerSegment;
        const searchPool = reelItems.slice(startIndex, startIndex + prizesPerSegment);
        const indexInPool = searchPool.findIndex(p => p.name === targetItem.name);

        if (indexInPool !== -1) {
            winnerIndex = startIndex + indexInPool;
        } else {
            // Fallback if winner isn't in the target segment (should be rare)
            winnerIndex = reelItems.findIndex(p => p.name === targetItem.name) || 0;
        }

        const finalOffset = (winnerIndex * REEL_ITEM_WIDTH) - (containerWidth / 2) + (REEL_ITEM_WIDTH / 2) + OFFSET_FIX;
        scrollPosition.current = -finalOffset;
        setReelTransform(`translateX(${scrollPosition.current}px)`);

        setTimeout(() => {
            setTransitionEnabled(false);
            const newWinnerIndex = (winnerIndex % prizesPerSegment) + prizesPerSegment;
            const newOffset = (newWinnerIndex * REEL_ITEM_WIDTH) - (containerWidth / 2) + (REEL_ITEM_WIDTH / 2) + OFFSET_FIX;
            scrollPosition.current = -newOffset;
            setReelTransform(`translateX(${scrollPosition.current}px)`);
            
            setIsSpinning(false);
            setWonPrize(targetItem);
        }, 12000);
    };

    // Для Free Case: обработка открытия и установка таймера
    const handleFreeCaseSpin = () => {
      if (isSpinning || freeCaseTimeLeft > 0 || containerWidth === 0) return;

      setTransitionEnabled(true);
      setIsSpinning(true);

      const targetItem = selectWinner();
      const searchSegment = 3;
      let winnerIndex = -1;

      // Найти выигрышный элемент в reelItems
      const startIndex = searchSegment * prizesPerSegment;
      const searchPool = reelItems.slice(startIndex, startIndex + prizesPerSegment);
      const indexInPool = searchPool.findIndex(p => p.name === targetItem.name);

      if (indexInPool !== -1) {
        winnerIndex = startIndex + indexInPool;
      } else {
        winnerIndex = reelItems.findIndex(p => p.name === targetItem.name) || 0;
      }

      const finalOffset = (winnerIndex * REEL_ITEM_WIDTH) - (containerWidth / 2) + (REEL_ITEM_WIDTH / 2) + OFFSET_FIX;
      scrollPosition.current = -finalOffset;
      setReelTransform(`translateX(${scrollPosition.current}px)`);

      setTimeout(() => {
        setTransitionEnabled(false);
        const newWinnerIndex = (winnerIndex % prizesPerSegment) + prizesPerSegment;
        const newOffset = (newWinnerIndex * REEL_ITEM_WIDTH) - (containerWidth / 2) + (REEL_ITEM_WIDTH / 2) + OFFSET_FIX;
        scrollPosition.current = -newOffset;
        setReelTransform(`translateX(${scrollPosition.current}px)`);
        
        setIsSpinning(false);
        setWonPrize(targetItem);

        // Если выиграли звёзды — начисляем их на баланс
        if ((targetItem.name === '1 Star' || targetItem.name === '10 Stars') && typeof onAddStars === 'function') {
          onAddStars(targetItem.price);
        }

        // После выигрыша — ставим таймер
        const nextAvailable = Date.now() + FREE_CASE_COOLDOWN;
        localStorage.setItem(FREE_CASE_STORAGE_KEY, nextAvailable.toString());
        setFreeCaseTimeLeft(FREE_CASE_COOLDOWN);
        if (freeCaseTimerId) clearInterval(freeCaseTimerId);
        const timer = setInterval(() => {
          const left = nextAvailable - Date.now();
          setFreeCaseTimeLeft(left > 0 ? left : 0);
          if (left <= 0) {
            clearInterval(timer);
            setFreeCaseTimerId(null);
          }
        }, 1000);
        setFreeCaseTimerId(timer);
      }, 12000);
    };

    const handlePurchase = () => {
      if (balance >= caseItem.price) {
        // TODO: Implement actual purchase logic
        console.log(`Purchasing ${caseItem.name} for ${caseItem.price}`);
        // Here you would deduct balance, make API call, etc.
        // For now, we'll just log it.
      } else {
        onOpenBalanceModal();
      }
    };

    const handleTonPurchase = () => {
        // TODO: Implement TON purchase logic
        console.log(`Purchasing with TON`);
    };

    const handleCloseWinModal = () => {
        setWonPrize(null);
    };

    const handleWithdraw = () => {
        console.log("Withdrawing prize:", wonPrize);
        handleCloseWinModal();
    };

    const handleSell = () => {
        console.log("Selling prize:", wonPrize);
        handleCloseWinModal();
    };

    // Кнопка для Free Case
    const renderFreeCaseButton = () => {
      if (caseItem.name === 'Free Case' || caseItem._id === '6') {
        return (
          <button className="demo-spin-btn" onClick={handleFreeCaseSpin} disabled={isSpinning || freeCaseTimeLeft > 0}>
            {freeCaseTimeLeft > 0
              ? `Доступно через ${Math.floor(freeCaseTimeLeft / 1000 / 60 / 60)}ч ${Math.floor((freeCaseTimeLeft / 1000 / 60) % 60)}м ${Math.floor((freeCaseTimeLeft / 1000) % 60)}с`
              : (isSpinning ? t('spinning') : 'Открыть бесплатно')}
          </button>
        );
      }
      return null;
    };

    return (
        <div className="case-detail-container">
            <h2 className="case-detail-title">{t('case_contents')}</h2>

            {/* Reel Indicator - MOVED */}
            <div className="reel-indicator-container">
                <div className="reel-indicator"></div>
            </div>

            {/* Reel of potential prizes - UPDATED */}
            <div className="reel-container-nova" ref={reelContainerRef}>
                <div
                    className={`potential-prizes-reel ${transitionEnabled && 'transitioning'}`}
                    style={{ 
                        transform: reelTransform,
                        transition: transitionEnabled ? 'transform 12s cubic-bezier(0.2, 0.8, 0.2, 1)' : 'none'
                    }}
                >
                    {reelItems.map((prize, index) => (
                        <div key={index} className="potential-prize-item">
                            <img src={`/prizes/${prize.image}`} alt={prize.name} />
                            <div className="price-tag">
                                <img src="/icons/stars-symbol.png" alt="stars" className="case-star-icon-nova" style={{width: 16, height: 16}} /> {prize.price}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Кнопки покупки только для не Free Case */}
            {!(caseItem.name === 'Free Case' || caseItem._id === '6') && (
              <>
                <button className="purchase-btn-nova" onClick={handlePurchase}>
                    {t('purchase')} — {caseItem.price} <img src="/icons/stars-symbol.png" alt="stars" className="case-star-icon-nova" style={{width: 16, height: 16}} />
                </button>
                <button className="purchase-btn-nova ton-purchase-btn" onClick={handleTonPurchase}>
                    {t('purchase')} — {caseItem.tonPrice || calculateTonPrice(caseItem.price)} <img src="/icons/ton_symbol.png" alt="TON" className="ton-icon-btn" />
                </button>
                <button className="demo-spin-btn" onClick={handleSpin} disabled={isSpinning}>
                  {isSpinning ? t('spinning') : t('demo_spin')}
                </button>
              </>
            )}
            {renderFreeCaseButton()}

            {/* Main Prizes Section */}
            <h3 className="section-title">Главные призы</h3>
            <div className="main-prizes-grid">
                {mainPrizes.map((prize, index) => (
                  <div key={index} className="main-prize-card">
                    <img src={`/prizes/${prize.image}`} alt={prize.name} className="main-prize-img" />
                    <div className="price-tag">
                      <img src="/icons/stars-symbol.png" alt="stars" className="case-star-icon-nova" style={{width: 16, height: 16}} /> {prize.price}
                    </div>
                    <div className="price-tag-ton">
                      <img src="/icons/ton_symbol.png" alt="TON" className="price-ton-icon" />
                      {prize.tonPrice}
                    </div>
                  </div>
                ))}
            </div>

            {/* Small Prizes Section - UPDATED */}
            <h3 className="section-title">{t('other_prizes')}</h3>
            <div className="small-prizes-grid">
                {(caseItem.name === 'Free Case' || caseItem._id === '6' ? potentialPrizes : smallPrizes).map((prize, index) => (
                  <div key={index} className="small-prize-card">
                    <img
                      src={`/prizes/${prize.image}`}
                      alt={prize.name}
                      className="small-prize-card-img"
                    />
                    <div className="price-tag">
                      <img src="/icons/stars-symbol.png" alt="stars" className="case-star-icon-nova" style={{width: 16, height: 16}} /> {prize.price}
                    </div>
                    <div className="price-tag-ton">
                      <img src="/icons/ton_symbol.png" alt="TON" className="price-ton-icon" />
                      {prize.tonPrice}
                    </div>
                  </div>
                ))}
            </div>

            {/* Render the modal */}
            {wonPrize && (
                <WinModal
                    prize={wonPrize}
                    onClose={handleCloseWinModal}
                    onWithdraw={handleWithdraw}
                    onSell={handleSell}
                />
            )}
        </div>
    );
};

export default CaseDetail; 